import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { apiService } from '@/services/api'
import { selectSelectedTypeId } from '../csFlow/csFlowSlice'

// Flutter-style Menu Controller - exact match
interface MenuState {
  // Loading state
  isLoading: boolean
  
  // List of products
  products: any[]
  
  // قائمة الفئات
  categories: string[]
  
  // Selected category
  selectedCategory: string
  
  // Search query
  searchQuery: string
  
  // Filtered items
  selectedItems: any[]
  
  // Variable to save the current language
  currentLanguage: string
  
  // Branch and user type info
  branchId: number
  userTypeId: number
}

const initialState: MenuState = {
  isLoading: false,
  products: [],
  categories: [],
  selectedCategory: '',
  searchQuery: '',
  selectedItems: [],
  currentLanguage: 'ar',
  branchId: 1,
  userTypeId: 3,
}

// Flutter-style async thunks matching exact controller methods
export const fetchMenuData = createAsyncThunk(
  'menu/fetchMenuData',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState
      
      // Check if the user is a customer service user
      if (isCustomerServiceUser(state)) {
        // Check if CS flow is complete
        if (state.csFlow && 
            (!state.csFlow.isOrderTypeSelected || 
             !state.csFlow.isBranchSelected || 
             !state.csFlow.selectedBranchId ||
             state.csFlow.selectedBranchId.toString().toLowerCase() === 'undefined')) {
          // Don't load the menu without showing a notification
          return { products: [], categories: [], selectedItems: [] }
        }
      }

      const branchId = getBranchId(state)
      const typeId = getUserTypeId(state)

      // Determine endpoint based on user type
      const endpoint = isCustomerServiceUser(state) 
          ? `/customerService/menus/branches/${branchId}?typeId=${typeId}`
          : `/cashier/menus/branches/${branchId}?typeId=${typeId}`

      const response = await apiService.axios.get(endpoint)

      if (response.status === 200) {
        const data = response.data

        if (data?.data?.categories) {
          const categories = data.data.categories

          // Extract products and categories
          const products: any[] = []
          const categoryNames: string[] = []

          for (const category of categories) {
            // Add category name
            if (category.name) {
              categoryNames.push(category.name.toString())
            }

            // Add products of the category
            if (category.products) {
              const categoryProducts = category.products
              for (const product of categoryProducts) {
                // 🔒 Filter out products with missing fields from the beginning
                if (product.id &&
                    product.name &&
                    product.image &&
                    product.price) {
                  // Add category name to each product
                  product.category = category.name
                  products.push(product)
                }
              }
            }
          }

          return { 
            products, 
            categories: categoryNames, 
            selectedItems: products.filter(product => product.category === categoryNames[0])
          }
        } else {
          // In case of no valid data
          return { products: [], categories: [], selectedItems: [] }
        }
      } else {
        // In case of API call failure
        return { products: [], categories: [], selectedItems: [] }
      }
    } catch (error) {
      // In case of error
      return { products: [], categories: [], selectedItems: [] }
    }
  }
)

export const refreshDataAfterLanguageChange = createAsyncThunk(
  'menu/refreshDataAfterLanguageChange',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState
      
      // Check if the order details are complete before loading the menu
      if (isCustomerServiceUser(state)) {
        // Check if CS flow is complete
        if (state.csFlow && 
            (!state.csFlow.isOrderTypeSelected || 
             !state.csFlow.isBranchSelected || 
             !state.csFlow.selectedBranchId ||
             state.csFlow.selectedBranchId.toString().toLowerCase() === 'undefined')) {
          // Don't load the menu without showing a notification
          return { products: [], categories: [], selectedItems: [] }
        }
      }

      const branchId = getBranchId(state)
      const typeId = getUserTypeId(state)

      // Determine endpoint based on user type
      const endpoint = isCustomerServiceUser(state) 
          ? `/customerService/menus/branches/${branchId}?typeId=${typeId}`
          : `/cashier/menus/branches/${branchId}?typeId=${typeId}`

      // Call the API directly without delay with short timeout
      const response = await apiService.axios.get(endpoint, {
        timeout: 8000, // shorter timeout
      })

      if (response.status === 200) {
        const data = response.data

        if (data?.data?.categories) {
          const categories = data.data.categories

          // Extract products and categories
          const products: any[] = []
          const categoryNames: string[] = []

          for (const category of categories) {
            // Add category name
            if (category.name) {
              categoryNames.push(category.name.toString())
            }

            // Add products of the category
            if (category.products) {
              const categoryProducts = category.products
              for (const product of categoryProducts) {
                    // 🔒 Filter out products with missing fields from the beginning
                if (product.id &&
                    product.name &&
                    product.image &&
                    product.price) {
                  // Add category name to each product
                  product.category = category.name
                  products.push(product)
                }
              }
            }
          }

          return { 
            products, 
            categories: categoryNames, 
            selectedItems: products.filter(product => product.category === categoryNames[0])
          }
        } else {
          return { products: [], categories: [], selectedItems: [] }
        }
      } else {
        return { products: [], categories: [], selectedItems: [] }
      }
    } catch (error) {
      return { products: [], categories: [], selectedItems: [] }
    }
  }
)

// Flutter-style Menu Slice - exact match
const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    // Flutter-style reducers matching exact controller methods
    initializeMenuData: (state) => {
      // مسح البيانات الحالية أولاً
      state.products = []
      state.categories = []
      state.selectedItems = []
      state.selectedCategory = ''
      state.searchQuery = ''
    },

    selectCategory: (state, action: PayloadAction<string>) => {
      const category = action.payload
      if (state.selectedCategory !== category) {
        state.selectedCategory = category
        updateSelectedItems(state)
      }
    },

    updateSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
      updateSelectedItems(state)
    },

    clearSearch: (state) => {
      state.searchQuery = ''
      updateSelectedItems(state)
    },

    clearMenuData: (state) => {
      state.products = []
      state.categories = []
      state.selectedItems = []
      state.selectedCategory = ''
      state.searchQuery = ''
      state.isLoading = false
    },

    setCurrentLanguage: (state, action: PayloadAction<string>) => {
      state.currentLanguage = action.payload
    },

    setBranchId: (state, action: PayloadAction<number>) => {
      state.branchId = action.payload
    },

    setUserTypeId: (state, action: PayloadAction<number>) => {
      state.userTypeId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchMenuData.fulfilled, (state, action) => {
        state.isLoading = false
        state.products = action.payload.products
        state.categories = action.payload.categories
        state.selectedItems = action.payload.selectedItems
        
        // Select the first category as default
        if (state.categories.length > 0) {
          state.selectedCategory = state.categories[0]
        }
      })
      .addCase(fetchMenuData.rejected, (state) => {
        state.isLoading = false
        state.products = []
        state.categories = []
        state.selectedItems = []
        state.selectedCategory = ''
      })
      .addCase(refreshDataAfterLanguageChange.pending, (state) => {
        state.isLoading = true
      })
      .addCase(refreshDataAfterLanguageChange.fulfilled, (state, action) => {
        state.isLoading = false
        state.products = action.payload.products
        state.categories = action.payload.categories
        state.selectedItems = action.payload.selectedItems
        
        // تحديد الفئة الأولى كافتراضية
        if (state.categories.length > 0) {
          state.selectedCategory = state.categories[0]
        }
      })
      .addCase(refreshDataAfterLanguageChange.rejected, (state) => {
        state.isLoading = false
      })
  },
})

// Flutter-style helper functions - exact match
function isCustomerServiceUser(_state: RootState): boolean {
  try {
    const employeeDataStr = localStorage.getItem('employee_data')
    if (!employeeDataStr) return false

    const employeeData = JSON.parse(employeeDataStr)
    const userType = employeeData.user_type ?? employeeData.type ?? ''
    return userType.toString().toLowerCase().includes('cs') ||
           userType.toString().toLowerCase().includes('customer_service')
  } catch (e) {
    return false
  }
}

function getBranchId(state: RootState): number {
  try {
    // أولاً، محاولة الحصول على معرف الفرع من CsFlowController (للمستخدمين CS)
    if (state.csFlow?.selectedBranchId) {
      return state.csFlow.selectedBranchId
    }

    // ثانياً، الحصول على معرف الفرع من بيانات تسجيل الدخول (auth.user.branch.id)
    const user = state.auth.user
    if (user?.branch?.id) {
      const branchId = typeof user.branch.id === 'number' ? user.branch.id : Number(user.branch.id)
      if (!isNaN(branchId) && branchId > 0) {
        return branchId
      }
    }

    // Third, try to get it from localStorage (fallback)
    const employeeDataStr = localStorage.getItem('employee_data')
    if (employeeDataStr) {
      const employeeData = JSON.parse(employeeDataStr)
      if (employeeData.branch?.id) {
        const branchId = typeof employeeData.branch.id === 'number' 
          ? employeeData.branch.id 
          : Number(employeeData.branch.id)
        if (!isNaN(branchId) && branchId > 0) {
          return branchId
        }
      }
    }
  } catch (e) {
  }

  // In case of no data, return an error instead of default value
  throw new Error('Branch ID is required. Please ensure the user is logged in with branch information.')
}

function getUserTypeId(state: RootState): number {
  try {
    // First, try to get the type ID from CsFlowController
    if (state.csFlow?.isCsFlowComplete) {
      // Use selector to get selected type ID
      const selectedTypeId = selectSelectedTypeId(state)
      if (selectedTypeId) {
        return selectedTypeId
      }
    }

    // If not available, try to get it from cashier data
    const employeeDataStr = localStorage.getItem('employee_data')
    if (employeeDataStr) {
      const employeeData = JSON.parse(employeeDataStr)
      if (employeeData.user?.type_id) {
        return employeeData.user.type_id
      }
    }
  } catch (e) {
    // Removed debugPrint
  }

  // Default value in case of no data
  return 3
}

function updateSelectedItems(state: MenuState): void {
  let items: any[] = []

  if (state.searchQuery.length > 0) {
    // Search in all products
    items = state.products.filter((product) => {
      // 🔒 Filter out products with missing fields from the beginning
      if (!product.id ||
          !product.name ||
          !product.image ||
          !product.price) {
        return false // Ignore products with missing fields
      }

      const name = product.name?.toString().toLowerCase() ?? ''
      const description = product.description?.toString().toLowerCase() ?? ''
      const query = state.searchQuery.toLowerCase()
      return name.includes(query) || description.includes(query)
    })
  } else {
    // Display products based on the category
    items = state.products.filter((product) => {
      // 🔒 Filter out products with missing fields from the beginning
      if (!product.id ||
          !product.name ||
          !product.image ||
          !product.price) {
        return false // Ignore products with missing fields
      }

      return product.category?.toString() === state.selectedCategory
    })
  }

  // 🔒 Final filter to ensure no empty elements
  state.selectedItems = items.filter(
    (item) =>
        item != null &&
        typeof item === 'object' &&
        item.id != null &&
        item.name != null &&
        item.image != null &&
        item.price != null
  )
}

// Flutter-style selectors - exact match
export const selectProducts = (state: RootState) => state.menu.products
export const selectCategories = (state: RootState) => state.menu.categories
export const selectSelectedCategory = (state: RootState) => state.menu.selectedCategory
export const selectSearchQuery = (state: RootState) => state.menu.searchQuery
export const selectSelectedItems = (state: RootState) => state.menu.selectedItems
export const selectIsLoading = (state: RootState) => state.menu.isLoading
export const selectCurrentLanguage = (state: RootState) => state.menu.currentLanguage
export const selectBranchId = (state: RootState) => state.menu.branchId
export const selectUserTypeId = (state: RootState) => state.menu.userTypeId

export const {
  initializeMenuData,
  selectCategory,
  updateSearchQuery,
  clearSearch,
  clearMenuData,
  setCurrentLanguage,
  setBranchId,
  setUserTypeId,
} = menuSlice.actions

export default menuSlice.reducer